import React, { Component } from 'react';
import {
  Flex,
  MenuItem,
  Text,
  Button,
  DropdownMenu,
  Menu,
  Dialog,
  DialogFooter,
  ConfirmDialog,
  Icon,
  Spinner
} from 'core/components';
import styled from 'styled-components';
import { InputGroup } from 'core/form';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { fromNow } from 'core/util/dateUtils';
import SudoTag from 'app/components/SudoTag';

const KaizenMenuItemWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  li {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Positions the working spinner / pin in the left gutter of a list item
const GutterIndicator = styled.div`
  position: absolute;
  left: 5px;
  top: 50%;
  transform: translateY(-50%);
`;

// Solid equivalents of the Blueprint menu item hover/active tints over the sidebar background, so the floating
// row slot can cover the title with exactly the row's color
const denseRowColors = ({ theme }) => {
  const isLight = theme.name === 'light';
  return {
    base: theme.colors.cardBackground,
    hover: isLight ? '#eef0f2' : '#3d3f41',
    active: isLight ? '#dfe1e5' : '#4f5256'
  };
};

// Dense (sidebar) rows float a single slot over the right end of the title: the actions menu on hover,
// otherwise the working/pinned status. The title runs full width and is clipped (not ellipsized) at the edge.
const DenseMenuItemWrapper = styled(KaizenMenuItemWrapper)`
  li,
  .bp4-text-overflow-ellipsis {
    text-overflow: clip;
  }

  .row-title {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: clip;
  }

  .row-slot {
    background-color: ${(props) => denseRowColors(props).base};
  }

  .bp4-menu-item:hover,
  .bp4-menu-item:hover .row-slot {
    background-color: ${(props) => denseRowColors(props).hover};
  }

  .bp4-menu-item.bp4-active,
  .bp4-menu-item.bp4-active .row-slot {
    background-color: ${(props) => denseRowColors(props).active};
  }

  .bp4-menu-item-icon {
    color: ${({ theme }) => theme.colors.muted};
  }

  .row-actions {
    display: none;
  }

  /* the slot only covers the title when it has something to show */
  &:not(.has-status) .row-slot {
    display: none;
  }

  &:hover,
  &:focus-within,
  &:has(.bp4-popover-open) {
    .row-slot,
    .row-actions {
      display: flex;
    }

    .row-status {
      display: none;
    }
  }
`;

const DenseRowSlot = styled(Flex)`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 28px;
  border-radius: 0 2px 2px 0;
  align-items: center;
  justify-content: center;
`;

// Hollow bullet for sidebar rows, sized to sit in the 16px menu item icon slot
const RowBullet = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="3" fill="none" stroke="currentColor" strokeWidth="1.25" />
  </svg>
);

// Keeps clicks on the row actions (including its menu, which bubbles through the portal) from selecting the chat
const stopPropagation = (e) => e.stopPropagation();

const KaizenMenuItem = styled(MenuItem)`
  li {
    width: 100%;
  }

  &.bp4-menu-item {
    flex: 1;
    padding: ${({ isCopilotMode, $dense }) => {
      if (isCopilotMode) {
        return '6px 12px';
      }
      return $dense ? '5px 8px' : '12px 16px';
    }};
    align-items: center;
    position: relative;
    min-height: ${({ isCopilotMode, $dense }) => (isCopilotMode || $dense ? 'auto' : '50px')};
    /* spaces the bullet from the title in dense rows, since the margins are reset below */
    gap: ${({ $dense }) => ($dense ? '7px' : 0)};

    &::before,
    & > * {
      margin: 0;
    }
  }

  &.selected {
    .actions {
      background-color: ${(props) => (props.theme.name === 'light' ? '#dfe1e5' : '#4f5256')};
    }
  }
`;
@inject('$kaizen')
@withRouter
@observer
class KaizenListItem extends Component {
  state = {
    journeyTitle: '',
    isEditing: false,
    showRenameDialog: false,
    showDeleteDialog: false,
    renameTitle: '',
    renamingLoading: false
  };

  constructor(props) {
    super(props);
    this.state.journeyTitle = this.getDisplayTitle(props.journey);
  }

  getDisplayTitle(journey) {
    return journey?.get('user_title') || journey?.get('session_title') || 'AI Advisor';
  }

  handleRenameClick = () => {
    const { journey } = this.props;
    this.setState({
      showRenameDialog: true,
      renameTitle: this.getDisplayTitle(journey)
    });
  };

  handleRenameTitleChange = (e) => {
    this.setState({ renameTitle: e.target.value });
  };

  handleRenameConfirm = async () => {
    const { journey } = this.props;
    const { renameTitle } = this.state;

    this.setState({ renamingLoading: true });

    try {
      await journey.save({ user_title: renameTitle }, { patch: true });
      this.setState({ showRenameDialog: false, renameTitle: '', renamingLoading: false });
    } catch (error) {
      console.error('Error renaming chat session:', error);
      this.setState({ renamingLoading: false });
      // Could add error handling/toast here
    }
  };

  handleDeleteConfirm = async () => {
    const { journey, history, $kaizen, isCopilotMode } = this.props;

    try {
      await journey.destroy();
      this.setState({ showDeleteDialog: false });

      if (isCopilotMode) {
        // In copilot mode, just clear the selection so the copilot shows new-chat state
        $kaizen.chatSessionCollection.clearSelection();
      } else {
        // On the full AI Advisor page, navigate back to the session list
        history.push($kaizen.baseUrl);
      }
    } catch (error) {
      console.error('Error deleting chat session:', error);
    }
  };

  handleTogglePin = async () => {
    const { journey } = this.props;
    const currentPinnedState = journey?.get('pinned');

    try {
      await journey.save({ pinned: !currentPinnedState });
    } catch (error) {
      console.error('Error toggling pin status:', error);
      // Could add error handling/toast here
    }
  };

  handleRenameCancel = () => {
    this.setState({ showRenameDialog: false, renameTitle: '', renamingLoading: false });
  };

  handleDeleteClick = () => {
    this.setState({ showDeleteDialog: true });
  };

  handleDeleteCancel = () => {
    this.setState({ showDeleteDialog: false });
  };

  get workingSpinner() {
    const { journey } = this.props;
    if (!journey?.isAgentActive) {
      return null;
    }

    return (
      <GutterIndicator>
        <Spinner size={14} variant="hourglass" />
      </GutterIndicator>
    );
  }

  get pinnedIndicator() {
    const { journey } = this.props;
    const isPinned = journey?.get('pinned') && journey.isUserChat;

    // The working spinner takes precedence in the gutter; show the pin again when work is done
    if (!isPinned || journey?.isAgentActive) {
      return null;
    }

    return (
      <GutterIndicator>
        <Icon icon="pin" color="muted" iconSize={14} />
      </GutterIndicator>
    );
  }

  get denseStatusIndicator() {
    const { journey } = this.props;

    if (journey?.isAgentActive) {
      return <Spinner size={14} variant="hourglass" />;
    }

    if (journey?.get('pinned') && journey.isUserChat) {
      return <Icon icon="pin" color="muted" iconSize={14} />;
    }

    return null;
  }

  get chatDropdownMenu() {
    const { journey } = this.props;

    // only show pins for user chats
    const isPinned = journey?.get('pinned') && journey.isUserChat;

    if (!journey.isUserChat) {
      return null;
    }

    return (
      <DropdownMenu
        content={
          <Menu>
            {journey.isUserChat && (
              <MenuItem
                icon={isPinned ? 'unpin' : 'pin'}
                text={isPinned ? 'Unpin' : 'Pin'}
                onClick={this.handleTogglePin}
              />
            )}
            <MenuItem icon="edit" text="Rename" onClick={this.handleRenameClick} />
            <MenuItem icon="trash" intent="danger" text="Delete" onClick={this.handleDeleteClick} />
          </Menu>
        }
      >
        <Button
          icon="more"
          minimal
          small
          color="muted"
          ariaLabel="Chat actions"
          // vertical kebab, matching AdminTable's kebab column
          style={{ flexShrink: 0, transform: 'rotate(90deg)' }}
        />
      </DropdownMenu>
    );
  }

  get dialogs() {
    const { journey } = this.props;
    const { showRenameDialog, showDeleteDialog, renameTitle, renamingLoading } = this.state;

    return (
      <>
        <Dialog
          title="Rename Conversation"
          isOpen={showRenameDialog}
          onClose={this.handleRenameCancel}
          style={{ width: 400 }}
        >
          <div style={{ padding: '16px' }}>
            <InputGroup
              value={renameTitle}
              onChange={this.handleRenameTitleChange}
              placeholder="Enter new title..."
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button text="Cancel" onClick={this.handleRenameCancel} mr={1} />
            <Button
              text="Rename"
              intent="primary"
              onClick={this.handleRenameConfirm}
              disabled={!renameTitle.trim() || renamingLoading}
              loading={renamingLoading}
            />
          </DialogFooter>
        </Dialog>

        <ConfirmDialog
          title="Delete Conversation"
          model={journey}
          text={`Are you sure you want to delete ${this.getDisplayTitle(journey)}?`}
          isOpen={showDeleteDialog}
          onConfirm={this.handleDeleteConfirm}
          onCancel={this.handleDeleteCancel}
          confirmButtonText="Delete"
          confirmButtonIntent="danger"
        />
      </>
    );
  }

  render() {
    const { journey, hideUserName, hideEditedDate, onSelectJourney, isCopilotMode, dense } = this.props;
    const { isEditing, showRenameDialog, showDeleteDialog, renameTitle, renamingLoading } = this.state;
    const spinnerPaddingLeft = 8;

    if (isCopilotMode) {
      return (
        <>
          <KaizenMenuItemWrapper>
            <KaizenMenuItem
              isCopilotMode={isCopilotMode}
              text={
                <Flex
                  alignItems="center"
                  gap={1}
                  justifyContent="space-between"
                  width="100%"
                  style={{ paddingLeft: spinnerPaddingLeft }}
                >
                  {this.workingSpinner}
                  {this.pinnedIndicator}
                  <Flex alignItems="center" gap={1}>
                    {this.getDisplayTitle(journey)}
                    <Text muted small>
                      {fromNow(journey?.get('updated_at'))}
                    </Text>
                  </Flex>
                  {journey.isKProxyUserChat && <SudoTag small flex="none" ml="auto" alignSelf="center" />}
                </Flex>
              }
              onClick={() => {
                onSelectJourney(journey);
              }}
              active={journey.isSelected}
            />
            {this.chatDropdownMenu}
          </KaizenMenuItemWrapper>

          {/* Rename Dialog */}
          <Dialog
            title="Rename Conversation"
            isOpen={showRenameDialog}
            onClose={this.handleRenameCancel}
            style={{ width: 400 }}
          >
            <div style={{ padding: '16px' }}>
              <InputGroup
                value={renameTitle}
                onChange={this.handleRenameTitleChange}
                placeholder="Enter new title..."
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button text="Cancel" onClick={this.handleRenameCancel} mr={1} />
              <Button
                text="Rename"
                intent="primary"
                onClick={this.handleRenameConfirm}
                disabled={!renameTitle.trim() || renamingLoading}
                loading={renamingLoading}
              />
            </DialogFooter>
          </Dialog>

          <ConfirmDialog
            title="Delete Conversation"
            text={`Are you sure you want to delete ${this.getDisplayTitle(journey)}?`}
            isOpen={showDeleteDialog}
            onConfirm={this.handleDeleteConfirm}
            onCancel={this.handleDeleteCancel}
            confirmButtonText="Delete"
            confirmButtonIntent="danger"
          />
        </>
      );
    }

    if (dense) {
      const title = this.getDisplayTitle(journey);
      const statusIndicator = this.denseStatusIndicator;

      return (
        <>
          <DenseMenuItemWrapper className={classNames({ 'has-status': !!statusIndicator })}>
            <KaizenMenuItem
              $dense
              icon={RowBullet}
              className={classNames({ editing: isEditing, selected: journey.isSelected })}
              text={
                <>
                  <Text className="row-title" fontWeight={journey.isSelected ? 'bold' : 'normal'} title={title}>
                    {title}
                  </Text>
                  <DenseRowSlot className="row-slot" onClick={stopPropagation}>
                    <Flex className="row-status">{statusIndicator}</Flex>
                    <Flex className="row-actions">{this.chatDropdownMenu}</Flex>
                  </DenseRowSlot>
                </>
              }
              active={journey.isSelected}
              onClick={() => {
                onSelectJourney(journey);
              }}
            />
          </DenseMenuItemWrapper>
          {this.dialogs}
        </>
      );
    }

    return (
      <>
        <KaizenMenuItemWrapper>
          <KaizenMenuItem
            className={classNames({ editing: isEditing, selected: journey.isSelected })}
            text={
              <Flex flexDirection="column" style={{ paddingLeft: spinnerPaddingLeft }}>
                {this.workingSpinner}
                {this.pinnedIndicator}
                <Flex alignItems="center" gap={1}>
                  <Text fontWeight={journey.isSelected ? 'bold' : 'normal'} ellipsis>
                    {this.getDisplayTitle(journey)}
                  </Text>
                </Flex>
                {!hideEditedDate && (
                  <Text muted small>
                    Edited {fromNow(journey?.get('updated_at'))} ago
                  </Text>
                )}
                {!hideUserName && (
                  <Text muted small>
                    {journey?.get('user.user_full_name') || journey?.get('user.user_email')}
                  </Text>
                )}
              </Flex>
            }
            active={journey.isSelected}
            onClick={() => {
              onSelectJourney(journey);
            }}
          />
          {this.chatDropdownMenu}
        </KaizenMenuItemWrapper>

        {this.dialogs}
      </>
    );
  }
}

export default KaizenListItem;
